import { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { NotaIntervaloSlider } from '../components/NotaIntervaloSlider';
import { useRefetchWhenCatalogChanges } from '../contexts/AdminCatalogContext';
import { useAdminUnsaved } from '../contexts/AdminUnsavedContext';
import { PhoneInputWithDDI } from '../lib/PhoneInputWithDDI';
import { getCidades, getCidadeById, getRegiaoFromCidade, resolveCidadeId } from '../lib/experienciasStorage';
import { getEstabelecimentos } from '../lib/estabelecimentosStorage';
import { getAssociacoes, type Associacao } from '../lib/associacoesStorage';
import {
  getAgencias,
  getGuias,
  getGuiaById,
  getAgenciaById,
  getGuiasByAgencia,
  saveGuia,
  saveAgencia,
  addGuia,
  addAgencia,
  removeGuia,
  removeAgencia,
  setGuiaStatus,
  setAgenciaStatus,
  vincularGuiaAgencia,
  getDataValidadePrimeirosSocorrosIsoGuia,
  type Agencia,
  type Guia,
  type StatusCadastro,
  type PerfilFornecedor,
} from '../lib/guiasAgenciasStorage';
import { extractDataConclusaoCertificadoIa } from '../lib/certificadoDataIa';
import {
  getSolicitacoes,
  getSolicitacoesPendentes,
  setSolicitacaoStatus,
  BROADCAST_SOLICITACOES_FORNECEDOR,
  type SolicitacaoFornecedor,
  type TipoFornecedor,
} from '../lib/solicitacoesFornecedorStorage';
import {
  addAprovado,
  addPendente,
  removePendente,
  addRecusado,
  removeAprovado,
} from '../../../shared/lib/fornecedoresAprovadosStorage';
import { mergeCertificadosParaAdmin } from '../../../shared/lib/solicitacaoFornecedorPerfil';
import { normalizeFotosTrabalhoFornecedor, MAX_FOTOS_TRABALHO_FORNECEDOR } from '../../../shared/lib/fotosTrabalhoFornecedor';
import { addNotification } from '../../../shared/lib/notificationStorage';
import { syncParceirosToApi, enviarNotificacaoParceiro } from '../lib/parceirosApi';
import { FORNECEDOR_MSG_CADASTRO_INATIVO_PADRAO } from '../../../shared/lib/fornecedorAuthMessages';

type ViewMode = 'list' | 'form';
type FormType = 'guia' | 'agencia';
const MAX_IMAGE_SIZE_MB = 5;
/** Certificados e anexos «tipo documento»: qualquer extensão no explorador; limite de tamanho no código. */
const MAX_ANEXO_CERTIFICADO_MB = 10;
const MAX_ANEXO_DOCUMENTO_PARCEIRO_MB = 10;

type CertDocumentoTipo = 'primeiros_socorros' | 'guia_condutor' | 'outros';

interface FormCertificadoLinha {
  tipoDocumento: CertDocumentoTipo;
  outrosDetalhe?: string;
  descricao: string;
  anexo: string;
  nomeArquivo?: string;
  dataConclusao?: string;
  /** Chave estável só no cliente (não persistir) para atualizar a linha após leitura por IA. */
  _clientKey?: string;
}

const CERT_DOC_LABEL: Record<CertDocumentoTipo, string> = {
  primeiros_socorros: 'Primeiros socorros',
  guia_condutor: 'Certificado de Guia ou Condutor',
  outros: 'Outros',
};

function descricaoCertificadoPersist(t: CertDocumentoTipo, outros?: string): string {
  if (t === 'primeiros_socorros') return 'Primeiros socorros';
  if (t === 'guia_condutor') return 'Certificado de Guia ou Condutor';
  return (outros || '').trim() || 'Outros';
}

function inferirTipoCertificadoAdmin(desc: string, tipoJson?: CertDocumentoTipo): CertDocumentoTipo {
  if (tipoJson) return tipoJson;
  const d = (desc || '').toLowerCase();
  if (d.includes('primeiros')) return 'primeiros_socorros';
  if ((d.includes('guia') && d.includes('condutor')) || d.includes('condutor')) return 'guia_condutor';
  if (d.includes('conclusão') || d.includes('conclusao')) return 'guia_condutor';
  return 'outros';
}

type ListaSortKey =
  | 'tipo'
  | 'foto'
  | 'nome'
  | 'nota'
  | 'email'
  | 'telefone'
  | 'regiao'
  | 'cidade'
  | 'status'
  | 'dataLimite'
  | 'validadePs';

function buscaParceirosMatch(query: string, nome: string, email: string, telefone: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const nomeL = (nome || '').toLowerCase();
  const emailL = (email || '').toLowerCase();
  const telL = (telefone || '').toLowerCase();
  if (nomeL.includes(q) || emailL.includes(q) || telL.includes(q)) return true;
  const qDigits = q.replace(/\D/g, '');
  if (qDigits.length >= 3) {
    const tDigits = (telefone || '').replace(/\D/g, '');
    if (tDigits.includes(qDigits)) return true;
  }
  return false;
}

function ParceiroAvatarCell({ src, initials }: { src?: string; initials: string }) {
  const [broken, setBroken] = useState(false);
  const safe = (initials || '?').trim().slice(0, 2).toUpperCase() || '?';
  if (src && !broken) {
    return (
      <img
        src={src}
        alt=""
        className="w-9 h-9 rounded-full object-cover border border-gray-200 bg-white shrink-0"
        onError={() => setBroken(true)}
      />
    );
  }
  return (
    <div
      className="w-9 h-9 rounded-full bg-gray-200 border border-gray-200 flex items-center justify-center text-gray-600 text-[10px] font-semibold shrink-0"
      aria-hidden
    >
      {safe}
    </div>
  );
}

/** `https://wa.me/…` só com dígitos; números BR sem +55 recebem 55. */
function buildWhatsAppUrlFromPhone(raw: string): string | null {
  let digits = (raw ?? '').replace(/\D/g, '');
  if (digits.length < 10) return null;
  if (!digits.startsWith('55') && (digits.length === 10 || digits.length === 11)) {
    digits = `55${digits}`;
  }
  return `https://wa.me/${digits}`;
}

function TelefoneCellWhatsapp({ value }: { value: string }) {
  const t = (value ?? '').trim();
  if (!t || t === '—') {
    return <span className="text-gray-400">—</span>;
  }
  const href = buildWhatsAppUrlFromPhone(t);
  if (!href) {
    return (
      <span className="text-gray-700 whitespace-nowrap max-w-[10rem] truncate block" title={t}>
        {t}
      </span>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-emerald-700 hover:text-emerald-900 hover:underline font-medium whitespace-nowrap max-w-[10rem] truncate block"
      title={`Abrir WhatsApp: ${t}`}
    >
      {t}
    </a>
  );
}

type GuiaStatusDropdownState = { guiaId: string; top: number; left: number };

const PERFIS_PAINEL_PARCEIRO: { value: PerfilFornecedor; label: string }[] = [
  { value: 'GUIA', label: 'Guia' },
  { value: 'TERAPEUTA', label: 'Terapeuta' },
  { value: 'COMERCIO_RESTAURANTE', label: 'Comércio / Restaurante' },
  { value: 'GESTOR_ATRATIVO', label: 'Gestor de Atrativo' },
  { value: 'ANFITRIAO_HOSPEDAGEM', label: 'Anfitrião de Hospedagem' },
];

const ROTULO_TIPO_FORNECEDOR: Record<string, string> = {
  GUIA: 'Guia',
  TERAPEUTA: 'Terapeuta',
  COMERCIO_RESTAURANTE: 'Comércio / Restaurante',
  GESTOR_ATRATIVO: 'Gestor de Atrativo',
  ANFITRIAO_HOSPEDAGEM: 'Anfitrião de Hospedagem',
};

/** Valores do filtro «Tipo» na lista de parceiros (além de todos / guia / agência). */
type FiltroTipoListaParceiros = 'todos' | 'guia' | 'agencia' | PerfilFornecedor;

const OPCOES_FILTRO_TIPO_LISTA: { value: FiltroTipoListaParceiros; label: string }[] = [
  { value: 'todos', label: 'Todos' },
  { value: 'guia', label: 'Guia' },
  { value: 'agencia', label: 'Agência' },
  { value: 'TERAPEUTA', label: 'Terapeuta' },
  { value: 'COMERCIO_RESTAURANTE', label: 'Comerciante' },
  { value: 'GESTOR_ATRATIVO', label: 'Gestor' },
  { value: 'ANFITRIAO_HOSPEDAGEM', label: 'Anfitrião' },
];

function perfisGuiaEfetivos(g: Guia): PerfilFornecedor[] {
  if (g.perfis && g.perfis.length > 0) return [...g.perfis];
  return ['GUIA'];
}

/** Texto da coluna Tipo: todos os perfis do parceiro, na ordem do painel. */
function rotulosPerfisGuiaLista(g: Guia): string {
  const set = new Set(perfisGuiaEfetivos(g));
  const parts: string[] = [];
  for (const { value, label } of PERFIS_PAINEL_PARCEIRO) {
    if (set.has(value)) parts.push(label);
  }
  for (const p of set) {
    if (!PERFIS_PAINEL_PARCEIRO.some((x) => x.value === p)) parts.push(ROTULO_TIPO_FORNECEDOR[p] ?? p);
  }
  return parts.join(', ');
}

function guiaPassaFiltroTipoLista(g: Guia, filtro: FiltroTipoListaParceiros): boolean {
  if (filtro === 'todos' || filtro === 'agencia') return true;
  if (filtro === 'guia') return true;
  return perfisGuiaEfetivos(g).includes(filtro);
}

function fotosPayloadForPerfis(perfis: PerfilFornecedor[], guia: string[] | undefined, terapeuta: string[] | undefined) {
  const g = normalizeFotosTrabalhoFornecedor(guia);
  const t = normalizeFotosTrabalhoFornecedor(terapeuta);
  const usaFotosTrabalhoGuia =
    perfis.includes('GUIA') ||
    perfis.includes('GESTOR_ATRATIVO') ||
    perfis.includes('ANFITRIAO_HOSPEDAGEM');
  return {
    fotosTrabalhoGuia: usaFotosTrabalhoGuia ? (g.length ? g : undefined) : undefined,
    fotosTrabalhoTerapeuta: perfis.includes('TERAPEUTA') ? (t.length ? t : undefined) : undefined,
  };
}

export default function GuiasPage() {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [formType, setFormType] = useState<FormType>('guia');
  const [editingId, setEditingId] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { setDirty } = useAdminUnsaved();
  const isFirstFormRenderRef = useRef(true);
  const [guias, setGuias] = useState<Guia[]>([]);
  const [agencias, setAgencias] = useState<Agencia[]>([]);
  const [associacoes, setAssociacoes] = useState<Associacao[]>([]);
  const [filtroStatus, setFiltroStatus] = useState<string>('');
  const [filtroTipo, setFiltroTipo] = useState<FiltroTipoListaParceiros>('todos');
  const [buscaParceiros, setBuscaParceiros] = useState('');
  const [filtroRegiao, setFiltroRegiao] = useState('');
  const [filtroCidadeId, setFiltroCidadeId] = useState('');
  const [notaMinFiltro, setNotaMinFiltro] = useState(0);
  const [notaMaxFiltro, setNotaMaxFiltro] = useState(5);
  const [listaSortKey, setListaSortKey] = useState<ListaSortKey | null>(null);
  const [listaSortDir, setListaSortDir] = useState<'asc' | 'desc'>('asc');
  const [statusDropdown, setStatusDropdown] = useState<GuiaStatusDropdownState | null>(null);
  const statusDropdownRef = useRef<GuiaStatusDropdownState | null>(null);
  statusDropdownRef.current = statusDropdown;
  const [deleteGuiaTarget, setDeleteGuiaTarget] = useState<Guia | null>(null);
  const [deleteAgenciaTarget, setDeleteAgenciaTarget] = useState<Agencia | null>(null);
  const [inativarGuiaModal, setInativarGuiaModal] = useState<Guia | null>(null);
  const [inativarMotivoDraft, setInativarMotivoDraft] = useState('');
  const [form, setForm] = useState({
    nome: '',
    cpf: '',
    email: '',
    telefone: '',
    chavePix: '',
    baseCidadeId: '',
    imagemPerfil: '' as string,
    certificados: [] as FormCertificadoLinha[],
    agenciaId: '',
    perfis: [] as PerfilFornecedor[],
    resumoPerfil: '',
    estabelecimentoId: '',
    documentoIdentificacao: '' as string,
    documentoIdentidadeFornecedor: '' as string,
    fotosTrabalhoGuia: [] as string[],
    fotosTrabalhoTerapeuta: [] as string[],
  });
  const [certNovoTipo, setCertNovoTipo] = useState<CertDocumentoTipo>('primeiros_socorros');
  const [certNovoOutrosTexto, setCertNovoOutrosTexto] = useState('');
  const [formTab, setFormTab] = useState<
    'pessoais' | 'guia' | 'terapeuta' | 'comercio' | 'gestor_hospedagem'
  >('pessoais');
  const [agenciaForm, setAgenciaForm] = useState({
    cnpj: '',
    razaoSocial: '',
    email: '',
    enderecoCompleto: '',
    telefone: '',
    guiaIds: [] as string[],
  });

  const cidades = getCidades();
  const estabelecimentos = getEstabelecimentos();

  const [solicitacoesRev, setSolicitacoesRev] = useState(0);
  const todasSolicitacoes = useMemo(() => getSolicitacoes(), [solicitacoesRev]);
  const solicitacoesPendentes = useMemo(() => getSolicitacoesPendentes(), [solicitacoesRev]);

  useEffect(() => {
    let ch: BroadcastChannel | null = null;
    try {
      ch = new BroadcastChannel(BROADCAST_SOLICITACOES_FORNECEDOR);
      ch.onmessage = () => setSolicitacoesRev((n) => n + 1);
    } catch {
      /* ignore */
    }
    return () => ch?.close();
  }, []);

  useEffect(() => {
    if (formTab === 'guia' && !form.perfis.includes('GUIA')) setFormTab('pessoais');
    if (formTab === 'terapeuta' && !form.perfis.includes('TERAPEUTA')) setFormTab('pessoais');
    if (formTab === 'comercio' && !form.perfis.includes('COMERCIO_RESTAURANTE')) setFormTab('pessoais');
    const temGestorOuHospSemGuia =
      (form.perfis.includes('GESTOR_ATRATIVO') || form.perfis.includes('ANFITRIAO_HOSPEDAGEM')) &&
      !form.perfis.includes('GUIA');
    if (formTab === 'gestor_hospedagem' && !temGestorOuHospSemGuia) setFormTab('pessoais');
  }, [form.perfis, formTab]);

  const refresh = () => {
    setGuias(getGuias());
    setAgencias(getAgencias());
    setAssociacoes(getAssociacoes());
    setSolicitacoesRev((n) => n + 1);
  };

  const limparFiltrosLista = () => {
    setBuscaParceiros('');
    setFiltroStatus('');
    setFiltroTipo('todos');
    setFiltroRegiao('');
    setFiltroCidadeId('');
    setNotaMinFiltro(0);
    setNotaMaxFiltro(5);
  };

  const handleAprovarSolicitacao = (sol: SolicitacaoFornecedor) => {
    const email = sol.email.trim().toLowerCase();
    const temComercio = sol.roles.includes('COMERCIO_RESTAURANTE' as TipoFornecedor);
    const perfisRegistroGuia = sol.roles.filter((r) => r !== 'COMERCIO_RESTAURANTE') as PerfilFornecedor[];
    const precisaRegistroGuia = perfisRegistroGuia.length > 0;
    if (precisaRegistroGuia) {
      const cidades = getCidades();
      const primeiraCidade = cidades[0]?.id ?? '';
      if (!primeiraCidade) {
        alert('Cadastre ao menos uma cidade antes de aprovar parceiros.');
        return;
      }
      const p = sol.perfilSolicitacao;
      const baseId = resolveCidadeId((p?.baseCidadeId ?? '').trim() || primeiraCidade);
      const certificados = p ? mergeCertificadosParaAdmin(p) : [];
      const agenciaSol = (p?.agenciaId ?? '').trim() || undefined;
      const novo = addGuia({
        nome: sol.nome,
        cpf: (p?.cpf ?? '').trim() || 'A preencher',
        email: sol.email,
        telefone: (p?.telefone ?? '').trim(),
        chavePix: (p?.chavePix ?? '').trim(),
        baseCidadeId: baseId,
        status: 'aprovado',
        imagemPerfil: p?.imagemPerfil,
        ...fotosPayloadForPerfis(perfisRegistroGuia, p?.fotosTrabalhoGuia, p?.fotosTrabalhoTerapeuta),
        documentoIdentificacao: (p?.documentoOpcionalAnexo ?? '').trim() || undefined,
        documentoIdentidadeFornecedor: (p?.documentoIdentidadeFornecedor ?? '').trim() || undefined,
        certificados: certificados.length > 0 ? certificados : undefined,
        resumoPerfil: (p?.resumoPerfil ?? '').trim() || undefined,
        agenciaId: agenciaSol,
        perfis: perfisRegistroGuia.length > 0 ? perfisRegistroGuia : undefined,
      });
      if (agenciaSol) vincularGuiaAgencia(novo.id, agenciaSol);
      setSolicitacaoStatus(sol.id, 'aprovado', novo.id);
    }
    if (temComercio) {
      const nova = addAgencia({
        cnpj: 'A preencher',
        razaoSocial: sol.nome,
        email: sol.email,
        enderecoCompleto: '',
        status: 'aprovado',
        guiaIds: [],
        documentoIdentidadeFornecedor: (sol.perfilSolicitacao?.documentoIdentidadeFornecedor ?? '').trim() || undefined,
      });
      setSolicitacaoStatus(sol.id, 'aprovado', undefined, nova.id);
    }
    addAprovado(email);
    removePendente(email);
    addNotification({
      toEmail: email,
      title: 'Cadastro aprovado',
      description: 'Seu cadastro como parceiro foi aprovado. Você já pode acessar a área do parceiro.',
      type: 'success',
    });
    void enviarNotificacaoParceiro(email, 'aprovado');
    void syncParceirosToApi(getGuias(), getAgencias(), getAssociacoes());
    refresh();
  };

  const [recusarModal, setRecusarModal] = useState<{ sol: SolicitacaoFornecedor; mensagem: string } | null>(null);
  const [recusarParceiroModal, setRecusarParceiroModal] = useState<{ tipo: 'guia' | 'agencia'; id: string; email: string; nome: string; mensagem: string } | null>(null);

  const handleRecusarSolicitacao = (sol: SolicitacaoFornecedor, mensagem?: string) => {
    const email = sol.email.trim().toLowerCase();
    addRecusado(email);
    removeAprovado(email);
    removePendente(email);
    setSolicitacaoStatus(sol.id, 'recusado', undefined, undefined, mensagem);
    addNotification({
      toEmail: email,
      title: 'Cadastro reprovado',
      description: mensagem?.trim() ? `Seu cadastro não foi aprovado. Motivo: ${mensagem}` : 'Seu cadastro não foi aprovado no momento.',
      type: 'warning',
    });
    void enviarNotificacaoParceiro(email, 'recusado', mensagem);
    setRecusarModal(null);
    refresh();
  };
  useEffect(() => { refresh(); }, []);

  useRefetchWhenCatalogChanges(() => refresh());

  useEffect(() => {
    if (!statusDropdown) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest('[data-guia-status-toggle="true"]')) return;
      if (target?.closest('#guia-status-dropdown-portal')) return;
      setStatusDropdown(null);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [statusDropdown]);

  useEffect(() => {
    if (!statusDropdown) return;
    const guiaId = statusDropdown.guiaId;
    const updatePosition = () => {
      const cur = statusDropdownRef.current;
      if (!cur || cur.guiaId !== guiaId) return;
      const el = document.querySelector(`[data-status-dropdown-anchor="${guiaId}"]`) as HTMLElement | null;
      if (!el) {
        setStatusDropdown(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setStatusDropdown((prev) => (prev && prev.guiaId === guiaId ? { ...prev, top: r.bottom + 4, left: r.left } : prev));
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [statusDropdown?.guiaId]);

  const guiasFiltrados = useMemo(() => {
    return guias.filter((g) => {
      if (filtroStatus && g.status !== filtroStatus) return false;
      if (!buscaParceirosMatch(buscaParceiros, g.nome, g.email, g.telefone ?? '')) return false;
      if (filtroCidadeId && resolveCidadeId(g.baseCidadeId ?? '') !== filtroCidadeId) return false;
      if (filtroRegiao) {
        const cid = g.baseCidadeId ? getCidadeById(g.baseCidadeId) : null;
        const ok =
          !!cid &&
          (getRegiaoFromCidade(cid) === filtroRegiao || (cid.regioes ?? []).some((r) => r === filtroRegiao));
        if (!ok) return false;
      }
      const n = typeof g.notaAvaliacao === 'number' && !Number.isNaN(g.notaAvaliacao) ? g.notaAvaliacao : 0;
      const lo = Math.min(notaMinFiltro, notaMaxFiltro);
      const hi = Math.max(notaMinFiltro, notaMaxFiltro);
      if (n < lo || n > hi) return false;
      if (!guiaPassaFiltroTipoLista(g, filtroTipo)) return false;
      return true;
    });
  }, [guias, filtroStatus, filtroTipo, buscaParceiros, filtroCidadeId, filtroRegiao, notaMinFiltro, notaMaxFiltro]);

  const agenciasFiltradas = useMemo(() => {
    return agencias.filter((a) => {
      if (filtroStatus && a.status !== filtroStatus) return false;
      if (buscaParceiros.trim()) {
        const matchBasico = buscaParceirosMatch(buscaParceiros, a.razaoSocial, (a.email ?? '').trim(), a.telefone ?? '');
        const q = buscaParceiros.trim().toLowerCase();
        if (!matchBasico && !(a.cnpj ?? '').toLowerCase().includes(q)) return false;
      }
      if (filtroCidadeId) {
        const nomeC = getCidadeById(filtroCidadeId)?.cidade?.trim().toLowerCase() ?? '';
        if (nomeC && !(a.enderecoCompleto || '').toLowerCase().includes(nomeC)) return false;
      }
      if (filtroRegiao) {
        const addr = (a.enderecoCompleto || '').toLowerCase();
        const matches = getCidades().some((c) => {
          const inRegion = getRegiaoFromCidade(c) === filtroRegiao || (c.regioes ?? []).includes(filtroRegiao);
          if (!inRegion) return false;
          const nome = (c.cidade || '').trim().toLowerCase();
          return !!nome && addr.includes(nome);
        });
        if (!matches) return false;
      }
      return true;
    });
  }, [agencias, filtroStatus, buscaParceiros, filtroCidadeId, filtroRegiao]);

  type FornecedorRow =
    | { tipo: 'guia'; data: Guia }
    | { tipo: 'agencia'; data: Agencia }
    | { tipo: 'solicitacao'; data: SolicitacaoFornecedor };

  const solicitacoesFiltradas = useMemo(() => {
    return todasSolicitacoes.filter((s) => {
      if (s.status === 'aprovado') return false;
      if (filtroStatus && s.status !== filtroStatus) return false;
      const telSol = (s.perfilSolicitacao?.telefone ?? '').trim();
      if (!buscaParceirosMatch(buscaParceiros, s.nome, s.email, telSol)) return false;
      const p = s.perfilSolicitacao;
      const cidadeIdSol = (p?.baseCidadeId ?? '').trim();
      if (filtroCidadeId && resolveCidadeId(cidadeIdSol) !== filtroCidadeId) return false;
      if (filtroRegiao) {
        const cid = cidadeIdSol ? getCidadeById(cidadeIdSol) : null;
        const ok =
          !!cid &&
          (getRegiaoFromCidade(cid) === filtroRegiao || (cid.regioes ?? []).some((r) => r === filtroRegiao));
        if (!ok) return false;
      }
      if (filtroTipo === 'todos') return true;
      if (filtroTipo === 'guia') {
        return s.roles.some(
          (r) =>
            r === 'GUIA' ||
            r === 'TERAPEUTA' ||
            r === 'GESTOR_ATRATIVO' ||
            r === 'ANFITRIAO_HOSPEDAGEM'
        );
      }
      if (filtroTipo === 'agencia') {
        return s.roles.includes('COMERCIO_RESTAURANTE' as TipoFornecedor);
      }
      return s.roles.includes(filtroTipo as TipoFornecedor);
    });
  }, [todasSolicitacoes, filtroStatus, filtroTipo, buscaParceiros, filtroCidadeId, filtroRegiao]);

  const fornecedoresUnificados: FornecedorRow[] = useMemo(() => {
    const solRows: FornecedorRow[] = solicitacoesFiltradas
      .slice()
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .map((s) => ({ tipo: 'solicitacao', data: s }));
    const rows: FornecedorRow[] = [...solRows];
    if (filtroTipo === 'agencia') {
      agenciasFiltradas.forEach((a) => rows.push({ tipo: 'agencia', data: a }));
    } else if (filtroTipo === 'todos') {
      agenciasFiltradas.forEach((a) => rows.push({ tipo: 'agencia', data: a }));
      guiasFiltrados.forEach((g) => rows.push({ tipo: 'guia', data: g }));
    } else {
      guiasFiltrados.forEach((g) => rows.push({ tipo: 'guia', data: g }));
    }
    return rows;
  }, [solicitacoesFiltradas, filtroTipo, guiasFiltrados, agenciasFiltradas]);

  const formatDateBR = (dateInput?: string) => {
    if (!dateInput) return '—';
    const dateOnly = dateInput.slice(0, 10);
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOnly);
    if (match) {
      const d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
      return d.toLocaleDateString('pt-BR');
    }
    const parsed = new Date(dateInput);
    return isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString('pt-BR');
  };

  useEffect(() => {
    if (!filtroCidadeId) return;
    const c = getCidadeById(filtroCidadeId);
    if (!c) {
      setFiltroCidadeId('');
      return;
    }
    if (filtroRegiao) {
      const ok = getRegiaoFromCidade(c) === filtroRegiao || (c.regioes ?? []).includes(filtroRegiao);
      if (!ok) setFiltroCidadeId('');
    }
  }, [filtroRegiao, filtroCidadeId]);

  const regioesSelectOpcoes = useMemo(() => {
    const s = new Set<string>();
    for (const c of getCidades()) {
      const r = getRegiaoFromCidade(c);
      if (r) s.add(r);
      for (const rg of c.regioes ?? []) {
        const t = (rg || '').trim();
        if (t) s.add(t);
      }
    }
    return [...s].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [guias, agencias, solicitacoesRev]);

  const cidadesSelectFiltradas = useMemo(() => {
    const all = getCidades()
      .slice()
      .sort((a, b) => (a.cidade || '').localeCompare(b.cidade || '', 'pt-BR', { sensitivity: 'base' }));
    if (!filtroRegiao) return all;
    return all.filter(
      (c) => getRegiaoFromCidade(c) === filtroRegiao || (c.regioes ?? []).includes(filtroRegiao)
    );
  }, [filtroRegiao, guias, agencias, solicitacoesRev]);

  const parseTsLista = (dateInput?: string) => {
    if (!dateInput) return 0;
    const t = new Date(dateInput).getTime();
    return Number.isNaN(t) ? 0 : t;
  };

  const rowListaMeta = (row: FornecedorRow) => {
    if (row.tipo === 'solicitacao') {
      const sol = row.data;
      const p = sol.perfilSolicitacao;
      const cidadeId = (p?.baseCidadeId ?? '').trim();
      const cidadeBase = cidadeId ? getCidadeById(cidadeId) : null;
      const foto = (p?.imagemPerfil ?? '').trim();
      return {
        tipoRank: 0,
        tipoLabel: sol.roles.map((r) => ROTULO_TIPO_FORNECEDOR[r] ?? r).join(', '),
        hasFoto: !!foto,
        fotoUrl: foto,
        initials: sol.nome,
        nome: sol.nome,
        nota: null as number | null,
        email: sol.email,
        telefone: (p?.telefone ?? '').trim() || '—',
        regiao: getRegiaoFromCidade(cidadeBase) || '—',
        cidade: cidadeBase?.cidade || '—',
        statusKey: sol.status === 'pendente' ? 'pendente' : 'recusado',
        dataTs: parseTsLista(sol.createdAt),
        validadePsTs: 0,
      };
    }
    if (row.tipo === 'guia') {
      const g = row.data;
      const cidadeBase = g.baseCidadeId ? getCidadeById(g.baseCidadeId) : null;
      const foto = (g.imagemPerfil ?? '').trim();
      const dl = g.dataLimiteCadastro;
      const vPs = getDataValidadePrimeirosSocorrosIsoGuia(g);
      const n =
        typeof g.notaAvaliacao === 'number' && !Number.isNaN(g.notaAvaliacao)
          ? Math.min(5, Math.max(0, g.notaAvaliacao))
          : null;
      return {
        tipoRank: 1,
        tipoLabel: rotulosPerfisGuiaLista(g),
        hasFoto: !!foto,
        fotoUrl: foto,
        initials: g.nome,
        nome: g.nome,
        nota: n,
        email: g.email,
        telefone: (g.telefone ?? '').trim() || '—',
        regiao: getRegiaoFromCidade(cidadeBase) || '—',
        cidade: cidadeBase?.cidade || '—',
        statusKey: g.status,
        dataTs: parseTsLista(dl || g.updatedAt),
        validadePsTs: parseTsLista(vPs),
      };
    }
    const a = row.data;
    return {
      tipoRank: 2,
      tipoLabel: 'Agência',
      hasFoto: false,
      fotoUrl: '',
      initials: a.razaoSocial,
      nome: a.razaoSocial,
      nota: null as number | null,
      email: (a.email ?? '').trim() || '—',
      telefone: (a.telefone ?? '').trim() || '—',
      regiao: '—',
      cidade: '—',
      statusKey: a.status,
      dataTs: parseTsLista(a.updatedAt),
      validadePsTs: 0,
    };
  };

  const sortedFornecedores = useMemo(() => {
    const rows = [...fornecedoresUnificados];
    if (!listaSortKey) return rows;
    const key = listaSortKey;
    const dir = listaSortDir;
    const mul = dir === 'asc' ? 1 : -1;
    rows.sort((rowA, rowB) => {
      const a = rowListaMeta(rowA);
      const b = rowListaMeta(rowB);
      let cmp = 0;
      switch (key) {
        case 'tipo':
          cmp = a.tipoRank - b.tipoRank || a.tipoLabel.localeCompare(b.tipoLabel, 'pt-BR', { sensitivity: 'base' });
          break;
        case 'foto':
          cmp = a.hasFoto === b.hasFoto ? 0 : a.hasFoto ? 1 : -1;
          break;
        case 'nome':
          cmp = a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' });
          break;
        case 'email':
          cmp = a.email.localeCompare(b.email, 'pt-BR', { sensitivity: 'base' });
          break;
        case 'telefone':
          cmp = a.telefone.localeCompare(b.telefone, 'pt-BR', { sensitivity: 'base' });
          break;
        case 'regiao':
          cmp = a.regiao.localeCompare(b.regiao, 'pt-BR', { sensitivity: 'base' });
          break;
        case 'cidade':
          cmp = a.cidade.localeCompare(b.cidade, 'pt-BR', { sensitivity: 'base' });
          break;
        case 'nota': {
          const na = a.nota;
          const nb = b.nota;
          if (na == null && nb == null) cmp = 0;
          else if (na == null) cmp = 1;
          else if (nb == null) cmp = -1;
          else cmp = na - nb;
          break;
        }
        case 'status':
          cmp = a.statusKey.localeCompare(b.statusKey, 'pt-BR');
          break;
        case 'dataLimite':
          cmp = a.dataTs - b.dataTs;
          break;
        case 'validadePs':
          cmp = a.validadePsTs - b.validadePsTs;
          break;
        default:
          cmp = 0;
      }
      return cmp * mul;
    });
    return rows;
  }, [fornecedoresUnificados, listaSortKey, listaSortDir]);

  const toggleListaSort = (key: ListaSortKey) => {
    if (listaSortKey !== key) {
      setListaSortKey(key);
      setListaSortDir(
        key === 'dataLimite' || key === 'validadePs' || key === 'nota' || key === 'foto' ? 'desc' : 'asc'
      );
    } else {
      setListaSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    }
  };

  const thSortBtnClass =
    'inline-flex items-center gap-1 w-full text-left font-semibold py-1 -my-1 rounded hover:bg-orange-600/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80';

  const sortMark = (key: ListaSortKey) =>
    listaSortKey === key ? (listaSortDir === 'asc' ? '\u00a0\u2191' : '\u00a0\u2193') : '';

  useEffect(() => {
    const state = location.state as { fromNav?: boolean } | null;
    if (state?.fromNav && location.pathname === '/guias') {
      setViewMode('list');
      setEditingId(null);
      setDirty(false);
      isFirstFormRenderRef.current = true;
      navigate('/guias', { replace: true, state: {} });
    }
  }, [location.pathname, location.state, setDirty, navigate]);

  useEffect(() => {
    if (viewMode !== 'form') {
      isFirstFormRenderRef.current = true;
      return;
    }
    if (isFirstFormRenderRef.current) {
      isFirstFormRenderRef.current = false;
      return;
    }
    setDirty(true);
  }, [form, agenciaForm, formType, viewMode, setDirty]);

  const openNew = () => {
    isFirstFormRenderRef.current = true;
    setDirty(false);
    setEditingId(null);
    setForm({
      nome: '',
      cpf: '',
      email: '',
      telefone: '',
      chavePix: '',
      baseCidadeId: '',
      imagemPerfil: '',
      fotosTrabalhoGuia: [],
      fotosTrabalhoTerapeuta: [],
      certificados: [],
      agenciaId: '',
      perfis: [],
      resumoPerfil: '',
      estabelecimentoId: '',
      documentoIdentificacao: '',
      documentoIdentidadeFornecedor: '',
    });
    setCertNovoTipo('primeiros_socorros');
    setCertNovoOutrosTexto('');
    setFormTab('pessoais');
    setFormType('guia');
    setViewMode('form');
  };

  const openNewAgencia = () => {
    isFirstFormRenderRef.current = true;
    setDirty(false);
    setEditingId(null);
    setAgenciaForm({ cnpj: '', razaoSocial: '', email: '', enderecoCompleto: '', telefone: '', guiaIds: [] });
    setFormType('agencia');
    setViewMode('form');
  };

  const openEditAgencia = (a: Agencia) => {
    isFirstFormRenderRef.current = true;
    setDirty(false);
    setEditingId(a.id);
    setAgenciaForm({
      cnpj: a.cnpj,
      razaoSocial: a.razaoSocial,
      email: a.email ?? '',
      enderecoCompleto: a.enderecoCompleto,
      telefone: a.telefone ?? '',
      guiaIds: [...(a.guiaIds ?? [])],
    });
    setFormType('agencia');
    setViewMode('form');
  };

  const handleSaveAgencia = () => {
    if (!agenciaForm.cnpj.trim()) { alert('CNPJ é obrigatório.'); return; }
    if (!agenciaForm.razaoSocial.trim()) { alert('Razão Social é obrigatória.'); return; }
    if (!agenciaForm.email.trim()) { alert('E-mail é obrigatório.'); return; }
    if (editingId) {
      const existing = getAgenciaById(editingId);
      if (existing) {
        const antigos = existing.guiaIds ?? [];
        saveAgencia({
          ...existing,
          cnpj: agenciaForm.cnpj.trim(),
          razaoSocial: agenciaForm.razaoSocial.trim(),
          email: agenciaForm.email.trim(),
          enderecoCompleto: agenciaForm.enderecoCompleto.trim(),
          telefone: agenciaForm.telefone.trim() || undefined,
          guiaIds: agenciaForm.guiaIds,
        });
        antigos.forEach((id) => { if (!agenciaForm.guiaIds.includes(id)) vincularGuiaAgencia(id, null); });
        agenciaForm.guiaIds.forEach((id) => vincularGuiaAgencia(id, editingId));
      }
    } else {
      const nova = addAgencia({
        cnpj: agenciaForm.cnpj.trim(),
        razaoSocial: agenciaForm.razaoSocial.trim(),
        email: agenciaForm.email.trim(),
        enderecoCompleto: agenciaForm.enderecoCompleto.trim(),
        telefone: agenciaForm.telefone.trim() || undefined,
        status: 'pendente',
        guiaIds: agenciaForm.guiaIds,
      });
      agenciaForm.guiaIds.forEach((id) => vincularGuiaAgencia(id, nova.id));
    }
    setDirty(false);
    refresh();
    setViewMode('list');
    void syncParceirosToApi(getGuias(), getAgencias(), getAssociacoes());
  };

  const handleAprovarRecusarAgencia = (a: Agencia, status: 'aprovado' | 'recusado') => {
    if (status === 'recusado') {
      setRecusarParceiroModal({ tipo: 'agencia', id: a.id, email: a.email ?? '', nome: a.razaoSocial, mensagem: '' });
      return;
    }
    setAgenciaStatus(a.id, status);
    const email = (a.email ?? '').trim().toLowerCase();
    if (email) {
      addNotification({ toEmail: email, title: 'Cadastro aprovado', description: 'Seu cadastro como parceiro foi aprovado. Você já pode acessar a área do parceiro.', type: 'success' });
      void enviarNotificacaoParceiro(email, 'aprovado');
    }
    refresh();
    void syncParceirosToApi(getGuias(), getAgencias(), getAssociacoes());
  };

  const handleConfirmarRecusaParceiro = () => {
    if (!recusarParceiroModal) return;
    const { tipo, id, email, nome, mensagem } = recusarParceiroModal;
    const emailNorm = email.trim().toLowerCase();
    if (tipo === 'guia') {
      setGuiaStatus(id, 'recusado');
    } else {
      setAgenciaStatus(id, 'recusado');
    }
    if (emailNorm) {
      addRecusado(emailNorm);
      removeAprovado(emailNorm);
      removePendente(emailNorm);
      addNotification({
        toEmail: emailNorm,
        title: 'Cadastro reprovado',
        description: mensagem?.trim() ? `Seu cadastro não foi aprovado. Motivo: ${mensagem}` : 'Seu cadastro não foi aprovado no momento.',
        type: 'warning',
      });
      void enviarNotificacaoParceiro(emailNorm, 'recusado', mensagem?.trim() || undefined);
    }
    setRecusarParceiroModal(null);
    refresh();
    void syncParceirosToApi(getGuias(), getAgencias(), getAssociacoes());
  };

  const toggleGuiaAgencia = (guiaId: string) => {
    setAgenciaForm((f) => ({
      ...f,
      guiaIds: f.guiaIds.includes(guiaId) ? f.guiaIds.filter((id) => id !== guiaId) : [...f.guiaIds, guiaId],
    }));
  };

  const openEdit = (g: Guia) => {
    isFirstFormRenderRef.current = true;
    setDirty(false);
    setEditingId(g.id);
    setFormType('guia');
    setForm({
      nome: g.nome,
      cpf: g.cpf,
      email: g.email,
      telefone: g.telefone,
      chavePix: g.chavePix,
      baseCidadeId: resolveCidadeId(g.baseCidadeId ?? ''),
      imagemPerfil: g.imagemPerfil ?? '',
      certificados: (() => {
        const certs = g.certificados ?? [];
        if (certs.length > 0) {
          return certs.map((c) => {
            const raw = c as FormCertificadoLinha & { dataValidade?: string };
            const tipoDocumento = inferirTipoCertificadoAdmin(c.descricao, raw.tipoDocumento);
            const dataConclusao = (c.dataConclusao ?? raw.dataValidade)?.slice(0, 10);
            const inferredOutros = inferirTipoCertificadoAdmin(c.descricao) === 'outros';
            const outrosDetalhe =
              tipoDocumento === 'outros'
                ? (raw.outrosDetalhe || (inferredOutros ? c.descricao : '')) || undefined
                : undefined;
            return {
              tipoDocumento,
              outrosDetalhe: outrosDetalhe?.trim() || undefined,
              descricao: c.descricao,
              anexo: c.anexo,
              nomeArquivo: c.nomeArquivo,
              dataConclusao,
            };
          });
        }
        if (g.certificadoAnexo) {
          return [{
            tipoDocumento: 'primeiros_socorros' as const,
            descricao: 'Primeiros socorros',
            anexo: g.certificadoAnexo,
            nomeArquivo: undefined,
            dataConclusao: g.certificadoDataConclusao?.slice(0, 10),
          }];
        }
        return [];
      })(),
      agenciaId: g.agenciaId ?? '',
      perfis: g.perfis ?? [],
      resumoPerfil: g.resumoPerfil ?? '',
      estabelecimentoId: g.estabelecimentoId ?? '',
      documentoIdentificacao: g.documentoIdentificacao ?? '',
      documentoIdentidadeFornecedor: g.documentoIdentidadeFornecedor ?? '',
      fotosTrabalhoGuia: g.fotosTrabalhoGuia ?? [],
      fotosTrabalhoTerapeuta: g.fotosTrabalhoTerapeuta ?? [],
    });
    setCertNovoTipo('primeiros_socorros');
    setCertNovoOutrosTexto('');
    setFormTab('pessoais');
    setViewMode('form');
  };

  const handleAprovarCidadeBase = (guiaId: string) => {
    const g = getGuiaById(guiaId);
    if (!g?.baseCidadePendenteAprovacao) return;
    const novaBase = g.baseCidadePendenteAprovacao;
    const baseCanon = resolveCidadeId(novaBase);
    saveGuia({
      ...g,
      baseCidadeId: baseCanon,
      baseCidadePendenteAprovacao: undefined,
      status: 'aprovado',
    });
    if (editingId === guiaId) setForm((f) => ({ ...f, baseCidadeId: baseCanon }));
    refresh();
  };

  const handleRejeitarCidadeBase = (guiaId: string) => {
    const g = getGuiaById(guiaId);
    if (!g) return;
    saveGuia({ ...g, baseCidadePendenteAprovacao: undefined, status: 'inativo' });
    refresh();
  };

  const handleSave = () => {
    if (!form.nome.trim()) { alert('Nome é obrigatório.'); return; }
    if (!form.cpf.trim()) { alert('CPF é obrigatório.'); return; }
    if (!form.email.trim()) { alert('E-mail é obrigatório.'); return; }
    if (!form.baseCidadeId) { alert('BASE (cidade) é obrigatória.'); return; }
    if (form.perfis.length === 0) {
      alert('Selecione ao menos um perfil em Definição de perfil.');
      return;
    }
    if (form.perfis.includes('COMERCIO_RESTAURANTE') && !form.estabelecimentoId.trim()) {
      alert('Comércio / Restaurante precisa se associar a algum estabelecimento.');
      return;
    }
    const soGestorOuHospSemGuiaTerapeutaComercio =
      !form.perfis.includes('GUIA') &&
      !form.perfis.includes('TERAPEUTA') &&
      !form.perfis.includes('COMERCIO_RESTAURANTE') &&
      (form.perfis.includes('GESTOR_ATRATIVO') || form.perfis.includes('ANFITRIAO_HOSPEDAGEM'));
    if (soGestorOuHospSemGuiaTerapeutaComercio) {
      if (!form.resumoPerfil.trim()) {
        alert('Preencha o resumo do perfil na aba Gestor / Hospedagem.');
        return;
      }
      if ((form.fotosTrabalhoGuia ?? []).filter((x) => (x || '').trim()).length < 1) {
        alert('Inclua ao menos uma foto do trabalho na aba Gestor / Hospedagem.');
        return;
      }
    }
    if (form.agenciaId && !form.documentoIdentificacao) {
      alert('Se escolher associação é necessário anexar documento de identificação.');
      return;
    }
    if (!form.documentoIdentidadeFornecedor?.trim()) {
      alert('Anexe o documento de identidade (RG, CNH ou documento com CPF) do parceiro. Campo obrigatório.');
      return;
    }
    if (!form.imagemPerfil?.trim()) {
      alert('Envie a foto de perfil 3x4 do parceiro.');
      return;
    }
    const payload = {
      nome: form.nome.trim(),
      cpf: form.cpf.trim(),
      email: form.email.trim(),
      telefone: form.telefone.trim(),
      chavePix: form.chavePix.trim(),
      baseCidadeId: resolveCidadeId(form.baseCidadeId),
      imagemPerfil: form.imagemPerfil || undefined,
      ...fotosPayloadForPerfis(form.perfis, form.fotosTrabalhoGuia, form.fotosTrabalhoTerapeuta),
      certificados: form.certificados.length > 0
        ? form.certificados.map((c) => {
            const { _clientKey: _omitClient, ...linha } = c as FormCertificadoLinha & { _clientKey?: string };
            return {
              descricao: descricaoCertificadoPersist(linha.tipoDocumento, linha.outrosDetalhe),
              anexo: linha.anexo,
              nomeArquivo: linha.nomeArquivo?.trim() || undefined,
              dataConclusao: linha.dataConclusao?.trim().slice(0, 10) || undefined,
              tipoDocumento: linha.tipoDocumento,
              ...(linha.tipoDocumento === 'outros' && (linha.outrosDetalhe || '').trim()
                ? { outrosDetalhe: (linha.outrosDetalhe || '').trim() }
                : {}),
            };
          })
        : undefined,
      agenciaId: undefined,
      perfis: form.perfis.length > 0 ? form.perfis : undefined,
      resumoPerfil: form.resumoPerfil.trim() || undefined,
      estabelecimentoId: form.estabelecimentoId.trim() || undefined,
      documentoIdentificacao: form.documentoIdentificacao || undefined,
      documentoIdentidadeFornecedor: form.documentoIdentidadeFornecedor.trim() || undefined,
    };
    const agenciaSelecionada = form.agenciaId || null;
    if (editingId) {
      const existing = getGuiaById(editingId);
      if (existing) {
        saveGuia({ ...existing, ...payload });
        vincularGuiaAgencia(editingId, agenciaSelecionada);
      }
    } else {
      const novo = addGuia({ ...payload, status: 'pendente' });
      vincularGuiaAgencia(novo.id, agenciaSelecionada);
      const em = payload.email.trim().toLowerCase();
      if (em) {
        removeAprovado(em);
        addPendente(em);
      }
    }
    setDirty(false);
    refresh();
    setViewMode('list');
    if (!editingId) void syncParceirosToApi(getGuias(), getAgencias(), getAssociacoes());
  };

  const handleAprovarRecusar = (g: Guia, status: 'aprovado' | 'recusado') => {
    if (status === 'recusado') {
      setRecusarParceiroModal({ tipo: 'guia', id: g.id, email: g.email, nome: g.nome, mensagem: '' });
      return;
    }
    const gFull = getGuiaById(g.id);
    if (gFull) saveGuia({ ...gFull, status: 'aprovado', motivoInatividade: undefined });
    else setGuiaStatus(g.id, 'aprovado');
    const email = g.email.trim().toLowerCase();
    addAprovado(email);
    removePendente(email);
    addNotification({ toEmail: email, title: 'Cadastro aprovado', description: 'Seu cadastro como parceiro foi aprovado. Você já pode acessar a área do parceiro.', type: 'success' });
    void enviarNotificacaoParceiro(email, 'aprovado');
    refresh();
    void syncParceirosToApi(getGuias(), getAgencias(), getAssociacoes());
  };

  const handleSetAtivoInativo = (id: string, next: 'ativo' | 'inativo', motivoInativo?: string) => {
    const g0 = getGuiaById(id);
    if (!g0) return;
    const emailKey = (g0.email || '').trim().toLowerCase();

    if (next === 'ativo') {
      saveGuia({ ...g0, status: 'aprovado', motivoInatividade: undefined });
      if (emailKey) {
        addAprovado(emailKey);
        removePendente(emailKey);
      }
    } else {
      const motivo = (motivoInativo?.trim() || FORNECEDOR_MSG_CADASTRO_INATIVO_PADRAO);
      saveGuia({ ...g0, status: 'inativo', motivoInatividade: motivo });
      if (emailKey) {
        removeAprovado(emailKey);
        removePendente(emailKey);
      }
      void enviarNotificacaoParceiro(emailKey, 'inativo', motivo);
    }
    refresh();
    setStatusDropdown(null);
    void syncParceirosToApi(getGuias(), getAgencias(), getAssociacoes());
  };

  const handleConfirmDeleteGuia = () => {
    if (!deleteGuiaTarget) return;
    vincularGuiaAgencia(deleteGuiaTarget.id, null);
    removeGuia(deleteGuiaTarget.id);
    setDeleteGuiaTarget(null);
    refresh();
    void syncParceirosToApi(getGuias(), getAgencias(), getAssociacoes());
  };

  const handleConfirmDeleteAgencia = () => {
    if (!deleteAgenciaTarget) return;
    removeAgencia(deleteAgenciaTarget.id);
    setDeleteAgenciaTarget(null);
    refresh();
    void syncParceirosToApi(getGuias(), getAgencias(), getAssociacoes());
  };

  const toWhatsappHref = (rawPhone?: string): string | null => {
    if (!rawPhone) return null;
    let digits = rawPhone.replace(/\D/g, '');
    if (!digits) return null;
    if (digits.length === 10 || digits.length === 11) digits = `55${digits}`;
    return `https://wa.me/${digits}`;
  };

  const formatNotaGuia = (nota?: number): string => {
    const n = typeof nota === 'number' && !Number.isNaN(nota) ? Math.min(5, Math.max(0, nota)) : 0;
    return n.toFixed(2);
  };

  const IconPencil = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
  );
  const IconTrash = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
  );
  const IconTrashCert = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );

  const handleImageFile = (e: React.ChangeEvent<HTMLInputElement>, field: 'imagemPerfil' | 'certificadoAnexo') => {
    const file = e.target.files?.[0];
    if (!file) return;
    const maxBytes = MAX_IMAGE_SIZE_MB * 1024 * 1024;
    if (file.size > maxBytes) { alert(`Arquivo até ${MAX_IMAGE_SIZE_MB}MB.`); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, [field]: reader.result as string }));
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleWorkPhotoFile = (e: React.ChangeEvent<HTMLInputElement>, field: 'fotosTrabalhoGuia' | 'fotosTrabalhoTerapeuta') => {
    const file = e.target.files?.[0];
    if (!file) return;
    const maxBytes = MAX_IMAGE_SIZE_MB * 1024 * 1024;
    if (file.size > maxBytes) { alert(`Arquivo até ${MAX_IMAGE_SIZE_MB}MB.`); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setForm((f) => {
        const cur = [...(f[field] ?? [])];
        if (cur.length >= MAX_FOTOS_TRABALHO_FORNECEDOR) return f;
        cur.push(dataUrl);
        return { ...f, [field]: cur };
      });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const removeWorkPhoto = (field: 'fotosTrabalhoGuia' | 'fotosTrabalhoTerapeuta', index: number) => {
    setForm((f) => ({
      ...f,
      [field]: (f[field] ?? []).filter((_, i) => i !== index),
    }));
  };

  const statusLabel: Record<StatusCadastro, string> = {
    pendente: 'Em análise',
    aprovado: 'Ativo',
    recusado: 'Recusado',
    inativo: 'Inativo',
  };

  const btnAprovarClass =
    'px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 shadow-sm transition-colors';
  const btnRecusarClass =
    'px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 shadow-sm transition-colors';

  const acoesIconBtnClass =
    'inline-flex items-center justify-center p-2.5 rounded-xl text-gray-600 hover:bg-gray-100 border border-transparent hover:border-gray-200 transition-colors';

  const renderSecaoDocumentosCertificados = () => (
    <section className="bg-gray-50 rounded-xl p-6 border border-gray-200">
      <h2 className="text-lg font-semibold text-gray-800 mb-2">Documentos anexados</h2>
      <p className="text-xs text-gray-500 mb-4">
        Escolha o tipo de documento, anexe o ficheiro e registe a data de conclusão em cada linha. São aceites imagens, PDF, Word (.doc/.docx), Excel, OpenDocument, RTF, texto e outros formatos (até {MAX_ANEXO_CERTIFICADO_MB}MB por ficheiro). Com chave OpenAI, a data de conclusão pode ser preenchida automaticamente só a partir de imagens. Em «Outros», descreva o documento antes de anexar.
      </p>

      <div className="space-y-3 mb-6">
        {form.certificados.map((cert, idx) => (
          <div
            key={cert._clientKey ?? `cert-${idx}`}
            className="bg-white border border-gray-200 rounded-lg px-3 py-2.5"
          >
            <p className="text-xs font-semibold text-gray-700 mb-2">{CERT_DOC_LABEL[cert.tipoDocumento]}</p>
            <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
              <div className="flex-1 min-w-[10rem]">
                <span className="sr-only">Nome do ficheiro</span>
                <span
                  className="text-sm text-gray-900 block truncate max-w-[min(100%,22rem)] sm:max-w-[28rem]"
                  title={cert.nomeArquivo}
                >
                  {(cert.nomeArquivo || '').trim() || '—'}
                </span>
              </div>
              <div className="flex items-end gap-2 shrink-0">
                <div className="flex flex-col gap-0.5">
                  <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Data de conclusão</label>
                  <input
                    type="date"
                    value={cert.dataConclusao ?? ''}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        certificados: f.certificados.map((c, i) =>
                          i === idx ? { ...c, dataConclusao: e.target.value || undefined } : c
                        ),
                      }))
                    }
                    className="py-1.5 px-2 rounded-lg border border-gray-300 text-sm"
                  />
                </div>
                <button
                  type="button"
                  title="Excluir documento"
                  onClick={() => setForm((f) => ({ ...f, certificados: f.certificados.filter((_, i) => i !== idx) }))}
                  className="inline-flex items-center justify-center p-2 rounded-lg text-gray-600 hover:bg-gray-100 border border-transparent hover:border-gray-200 hover:text-red-600 transition-colors mb-px"
                >
                  <IconTrashCert />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-dashed border-orange-200 rounded-xl p-4 space-y-3">
        <p className="text-sm font-medium text-gray-800">Anexar novo documento</p>
        <div className="flex flex-wrap gap-x-4 gap-y-2 items-center">
          <label className="flex items-center gap-2 cursor-pointer text-sm whitespace-nowrap">
            <input
              type="radio"
              name="certNovoTipoParceiro"
              checked={certNovoTipo === 'primeiros_socorros'}
              onChange={() => setCertNovoTipo('primeiros_socorros')}
              className="text-orange-500 border-gray-300"
            />
            Primeiros socorros
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-sm whitespace-nowrap">
            <input
              type="radio"
              name="certNovoTipoParceiro"
              checked={certNovoTipo === 'guia_condutor'}
              onChange={() => setCertNovoTipo('guia_condutor')}
              className="text-orange-500 border-gray-300"
            />
            Certificado de Guia ou Condutor
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-sm whitespace-nowrap">
            <input
              type="radio"
              name="certNovoTipoParceiro"
              checked={certNovoTipo === 'outros'}
              onChange={() => setCertNovoTipo('outros')}
              className="text-orange-500 border-gray-300"
            />
            Outros
          </label>
        </div>
        {certNovoTipo === 'outros' && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Descreva o documento *</label>
            <input
              type="text"
              value={certNovoOutrosTexto}
              onChange={(e) => setCertNovoOutrosTexto(e.target.value)}
              className="w-full py-2 px-3 rounded-lg border border-gray-300 text-sm"
              placeholder="Ex.: Curso de massoterapia, brevete náutico…"
            />
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Ficheiro (qualquer formato comum)</label>
          <input
            type="file"
            className="text-sm"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              if (certNovoTipo === 'outros' && !certNovoOutrosTexto.trim()) {
                alert('Em «Outros», preencha a descrição do documento antes de anexar o ficheiro.');
                e.target.value = '';
                return;
              }
              const maxBytes = MAX_ANEXO_CERTIFICADO_MB * 1024 * 1024;
              if (file.size > maxBytes) {
                alert(`Arquivo até ${MAX_ANEXO_CERTIFICADO_MB}MB.`);
                e.target.value = '';
                return;
              }
              const reader = new FileReader();
              reader.onload = () => {
                const desc = descricaoCertificadoPersist(certNovoTipo, certNovoOutrosTexto);
                const dataUrl = reader.result as string;
                const clientKey = `ck-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
                const mime = file.type || 'application/octet-stream';
                setForm((f) => ({
                  ...f,
                  certificados: [
                    ...f.certificados,
                    {
                      tipoDocumento: certNovoTipo,
                      outrosDetalhe: certNovoTipo === 'outros' ? certNovoOutrosTexto.trim() : undefined,
                      descricao: desc,
                      anexo: dataUrl,
                      nomeArquivo: file.name,
                      dataConclusao: undefined,
                      _clientKey: clientKey,
                    },
                  ],
                }));
                setCertNovoOutrosTexto('');
                e.target.value = '';
                void extractDataConclusaoCertificadoIa(dataUrl, mime).then((ymd) => {
                  if (!ymd) return;
                  setForm((f) => ({
                    ...f,
                    certificados: f.certificados.map((c) =>
                      (c as FormCertificadoLinha)._clientKey === clientKey ? { ...c, dataConclusao: ymd } : c
                    ),
                  }));
                });
              };
              reader.readAsDataURL(file);
            }}
          />
        </div>
      </div>
    </section>
  );

  return (
    <AdminLayout>
      <div className="w-full">
        <div className="sticky top-0 z-20 px-4 sm:px-6 py-6 border-b border-gray-200 bg-white shadow-sm">
          <nav className="text-sm text-gray-500 mb-1">
            <Link to="/dashboard" className="hover:text-gray-700">Home</Link>
            {' > '}
            <Link to="/guias" className="hover:text-gray-700">Parceiros</Link>
            {viewMode === 'form' && (
              <> {' > '} <span className="text-gray-800">{formType === 'agencia' ? (editingId ? 'Editar agência' : 'Nova agência') : (editingId ? 'Editar parceiro' : 'Novo parceiro')}</span></>
            )}
          </nav>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              {viewMode === 'list' ? 'Parceiros' : formType === 'agencia' ? (editingId ? 'Editar agência' : 'Nova agência') : (editingId ? 'Editar parceiro' : 'Cadastrar parceiro')}
            </h1>
            {viewMode === 'list' ? (
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={openNewAgencia} className="px-4 py-2 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-600">
                  Nova Agência
                </button>
                <button type="button" onClick={() => { openNew(); }} className="px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600">
                  Novo Parceiro
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <button type="button" onClick={() => { setDirty(false); setViewMode('list'); }} className="px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50">Cancelar</button>
                {formType === 'agencia' ? (
                  <button type="button" onClick={handleSaveAgencia} className="px-4 py-2 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-600">Salvar</button>
                ) : (
                  <button type="button" onClick={handleSave} className="px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600">Salvar</button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="px-4 sm:px-6 py-6">
          {viewMode === 'list' ? (
            <>
              {solicitacoesPendentes.length > 0 && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-950">
                  <strong>{solicitacoesPendentes.length}</strong> solicitação(ões) aguardando análise — aparecem na tabela abaixo com status <strong>Em análise</strong>.
                </div>
              )}
              <div className="flex flex-wrap gap-3 mb-4 items-end">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Busca</label>
                  <input
                    type="search"
                    placeholder="Nome, e-mail ou telefone"
                    value={buscaParceiros}
                    onChange={(e) => setBuscaParceiros(e.target.value)}
                    className="py-2 px-3 rounded-lg border border-gray-300 w-64 max-w-full"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Tipo</label>
                  <select
                    value={filtroTipo}
                    onChange={(e) => setFiltroTipo(e.target.value as FiltroTipoListaParceiros)}
                    className="py-2 px-3 rounded-lg border border-gray-300 min-w-[10rem]"
                  >
                    {OPCOES_FILTRO_TIPO_LISTA.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Região</label>
                  <select
                    value={filtroRegiao}
                    onChange={(e) => {
                      setFiltroRegiao(e.target.value);
                    }}
                    className="py-2 px-3 rounded-lg border border-gray-300 min-w-[10rem]"
                  >
                    <option value="">Todas</option>
                    {regioesSelectOpcoes.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Cidade</label>
                  <select
                    value={filtroCidadeId}
                    onChange={(e) => setFiltroCidadeId(e.target.value)}
                    className="py-2 px-3 rounded-lg border border-gray-300 min-w-[10rem]"
                  >
                    <option value="">Todas</option>
                    {cidadesSelectFiltradas.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.cidade}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-[11rem] max-w-[220px]">
                  <label className="block text-xs text-gray-500 mb-1">
                    Nota ({Math.min(notaMinFiltro, notaMaxFiltro).toFixed(1)} – {Math.max(notaMinFiltro, notaMaxFiltro).toFixed(1)})
                  </label>
                  <NotaIntervaloSlider
                    valueMin={notaMinFiltro}
                    valueMax={notaMaxFiltro}
                    onChange={(a, b) => {
                      setNotaMinFiltro(a);
                      setNotaMaxFiltro(b);
                    }}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Status</label>
                  <select
                    value={filtroStatus}
                    onChange={(e) => setFiltroStatus(e.target.value)}
                    className="py-2 px-3 rounded-lg border border-gray-300"
                  >
                    <option value="">Todos</option>
                    {(Object.keys(statusLabel) as StatusCadastro[]).map((s) => (
                      <option key={s} value={s}>
                        {statusLabel[s]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end pb-0.5">
                  <button
                    type="button"
                    onClick={limparFiltrosLista}
                    className="py-2 px-4 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Limpar
                  </button>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-visible">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1200px] text-sm">
                    <thead>
                      <tr className="bg-orange-500 text-white">
                        <th className="text-left py-3 px-3 font-semibold">
                          <button type="button" className={thSortBtnClass} onClick={() => toggleListaSort('tipo')}>
                            Tipo
                            <span className="text-xs font-normal opacity-90">{sortMark('tipo')}</span>
                          </button>
                        </th>
                        <th className="text-left py-3 px-2 font-semibold w-14">
                          <button type="button" className={thSortBtnClass} onClick={() => toggleListaSort('foto')}>
                            Foto
                            <span className="text-xs font-normal opacity-90">{sortMark('foto')}</span>
                          </button>
                        </th>
                        <th className="text-left py-3 px-4 font-semibold">
                          <button type="button" className={thSortBtnClass} onClick={() => toggleListaSort('nome')}>
                            Nome
                            <span className="text-xs font-normal opacity-90">{sortMark('nome')}</span>
                          </button>
                        </th>
                        <th className="text-left py-3 px-3 font-semibold">
                          <button type="button" className={thSortBtnClass} onClick={() => toggleListaSort('nota')}>
                            Nota
                            <span className="text-xs font-normal opacity-90">{sortMark('nota')}</span>
                          </button>
                        </th>
                        <th className="text-left py-3 px-4 font-semibold">
                          <button type="button" className={thSortBtnClass} onClick={() => toggleListaSort('email')}>
                            Email
                            <span className="text-xs font-normal opacity-90">{sortMark('email')}</span>
                          </button>
                        </th>
                        <th className="text-left py-3 px-3 font-semibold whitespace-nowrap">
                          <button type="button" className={thSortBtnClass} onClick={() => toggleListaSort('telefone')}>
                            Telefone
                            <span className="text-xs font-normal opacity-90">{sortMark('telefone')}</span>
                          </button>
                        </th>
                        <th className="text-left py-3 px-4 font-semibold">
                          <button type="button" className={thSortBtnClass} onClick={() => toggleListaSort('regiao')}>
                            Região
                            <span className="text-xs font-normal opacity-90">{sortMark('regiao')}</span>
                          </button>
                        </th>
                        <th className="text-left py-3 px-4 font-semibold">
                          <button type="button" className={thSortBtnClass} onClick={() => toggleListaSort('cidade')}>
                            Cidade
                            <span className="text-xs font-normal opacity-90">{sortMark('cidade')}</span>
                          </button>
                        </th>
                        <th className="text-left py-3 px-4 font-semibold">
                          <button type="button" className={thSortBtnClass} onClick={() => toggleListaSort('status')}>
                            Status
                            <span className="text-xs font-normal opacity-90">{sortMark('status')}</span>
                          </button>
                        </th>
                        <th className="text-left py-3 px-4 font-semibold">
                          <button type="button" className={thSortBtnClass} onClick={() => toggleListaSort('dataLimite')}>
                            Data limite
                            <span className="text-xs font-normal opacity-90">{sortMark('dataLimite')}</span>
                          </button>
                        </th>
                        <th className="text-left py-3 px-4 font-semibold whitespace-nowrap">
                          <button type="button" className={thSortBtnClass} onClick={() => toggleListaSort('validadePs')}>
                            Validade (PS)
                            <span className="text-xs font-normal opacity-90">{sortMark('validadePs')}</span>
                          </button>
                        </th>
                        <th className="text-right py-3 px-4 font-semibold whitespace-nowrap w-[7rem]">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedFornecedores.map((row, idx) => {
                        if (row.tipo === 'solicitacao') {
                          const sol = row.data;
                          const p = sol.perfilSolicitacao;
                          const cidadeId = (p?.baseCidadeId ?? '').trim();
                          const cidadeBase = cidadeId ? getCidadeById(cidadeId) : null;
                          const fotoSol = (p?.imagemPerfil ?? '').trim();
                          const isPendente = sol.status === 'pendente';
                          return (
                            <tr
                              key={`sol-${sol.id}`}
                              className={`${['bg-white', 'bg-gray-50', 'bg-orange-50'][idx % 3]} border-b border-gray-100 hover:opacity-90 transition-opacity`}
                            >
                              <td className="py-3 px-4">
                                <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-900">Solicitação</span>
                                <div className="text-[11px] text-gray-600 mt-1 max-w-[12rem]">
                                  {sol.roles.map((r) => ROTULO_TIPO_FORNECEDOR[r] ?? r).join(', ')}
                                </div>
                              </td>
                              <td className="py-3 px-2 align-middle">
                                <ParceiroAvatarCell src={fotoSol} initials={sol.nome} />
                              </td>
                              <td className="py-3 px-4">{sol.nome}</td>
                              <td className="py-3 px-3 text-gray-400">—</td>
                              <td className="py-3 px-4">{sol.email}</td>
                              <td className="py-3 px-3 align-middle">
                                <TelefoneCellWhatsapp value={(p?.telefone ?? '').trim() || '—'} />
                              </td>
                              <td className="py-3 px-4 text-gray-700">{getRegiaoFromCidade(cidadeBase) || '—'}</td>
                              <td className="py-3 px-4 text-gray-700">{cidadeBase?.cidade || '—'}</td>
                              <td className="py-3 px-4">
                                <div className="flex flex-col gap-2 items-start">
                                  {isPendente ? (
                                    <>
                                      <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">Em análise</span>
                                      <div className="flex flex-wrap gap-2">
                                        <button type="button" onClick={() => handleAprovarSolicitacao(sol)} className={btnAprovarClass}>
                                          Aprovar
                                        </button>
                                        <button type="button" onClick={() => setRecusarModal({ sol, mensagem: '' })} className={btnRecusarClass}>
                                          Recusar
                                        </button>
                                      </div>
                                    </>
                                  ) : (
                                    <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">Recusado</span>
                                  )}
                                </div>
                              </td>
                              <td className="py-3 px-4 text-gray-600">{formatDateBR(sol.createdAt)}</td>
                              <td className="py-3 px-4 text-gray-400">—</td>
                              <td className="py-3 px-4 text-right text-gray-400">—</td>
                            </tr>
                          );
                        }
                        if (row.tipo === 'guia') {
                          const g = row.data;
                          const dataLimiteCadastro = g.dataLimiteCadastro;
                          const validadePs = getDataValidadePrimeirosSocorrosIsoGuia(g);
                          const cidadeBase = g.baseCidadeId ? getCidadeById(g.baseCidadeId) : null;
                          const fotoGuia = (g.imagemPerfil ?? '').trim();
                          return (
                            <tr key={`guia-${g.id}`} className={`${['bg-white', 'bg-gray-50', 'bg-orange-50'][idx % 3]} border-b border-gray-100 hover:opacity-90 transition-opacity`}>
                              <td className="py-3 px-4">
                                <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">Parceiro</span>
                                <div className="text-[11px] text-gray-600 mt-1 max-w-[14rem] leading-snug">
                                  {rotulosPerfisGuiaLista(g)}
                                </div>
                              </td>
                              <td className="py-3 px-2 align-middle">
                                <ParceiroAvatarCell src={fotoGuia} initials={g.nome} />
                              </td>
                              <td className="py-3 px-4">{g.nome}</td>
                              <td className="py-3 px-3">
                                <span className="inline-flex items-center gap-1 text-amber-600" aria-label={`Avaliação ${formatNotaGuia(g.notaAvaliacao)} de 5`}>
                                  <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
                                  <span className="text-sm font-medium">{formatNotaGuia(g.notaAvaliacao)}</span>
                                </span>
                              </td>
                              <td className="py-3 px-4">{g.email}</td>
                              <td className="py-3 px-3 align-middle">
                                <TelefoneCellWhatsapp value={(g.telefone ?? '').trim() || '—'} />
                              </td>
                              <td className="py-3 px-4 text-gray-700">{getRegiaoFromCidade(cidadeBase) || '—'}</td>
                              <td className="py-3 px-4 text-gray-700">{cidadeBase?.cidade || '—'}</td>
                              <td className="py-3 px-4">
                                {g.status === 'pendente' ? (
                                  <div className="flex flex-col gap-2 items-start">
                                    <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 w-fit">Em análise</span>
                                    <div className="flex flex-wrap gap-2">
                                      <button type="button" onClick={() => handleAprovarRecusar(g, 'aprovado')} className={btnAprovarClass}>
                                        Aprovar
                                      </button>
                                      <button type="button" onClick={() => handleAprovarRecusar(g, 'recusado')} className={btnRecusarClass}>
                                        Recusar
                                      </button>
                                    </div>
                                  </div>
                                ) : g.status === 'aprovado' || g.status === 'inativo' ? (
                                  <div className="relative inline-block" data-guia-status-toggle="true" data-status-dropdown-anchor={g.id}>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        const r = e.currentTarget.getBoundingClientRect();
                                        setStatusDropdown((prev) =>
                                          prev?.guiaId === g.id ? null : { guiaId: g.id, top: r.bottom + 4, left: r.left }
                                        );
                                      }}
                                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all min-w-[110px] justify-between ${g.status === 'aprovado' ? 'bg-emerald-500/15 border-emerald-500 text-emerald-800' : 'bg-red-500/15 border-red-500 text-red-800'}`}
                                      aria-expanded={statusDropdown?.guiaId === g.id}
                                      aria-haspopup="listbox"
                                    >
                                      <span className={`relative inline-flex rounded-full h-2 w-2 ${g.status === 'aprovado' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                                      {g.status === 'aprovado' ? 'Ativo' : 'Inativo'}
                                      <svg className={`w-3.5 h-3.5 shrink-0 transition-transform ${statusDropdown?.guiaId === g.id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                    </button>
                                  </div>
                                ) : (
                                  <span
                                    className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                      g.status === 'recusado' ? 'bg-red-100 text-red-800' : 'bg-gray-200 text-gray-700'
                                    }`}
                                  >
                                    {statusLabel[g.status]}
                                  </span>
                                )}
                                {g.baseCidadePendenteAprovacao && <div className="mt-1 text-[11px] text-amber-700">Troca de BASE pendente: decidir ativar no editar.</div>}
                              </td>
                              <td className="py-3 px-4">{formatDateBR(dataLimiteCadastro)}</td>
                              <td className="py-3 px-4 text-gray-700 whitespace-nowrap">
                                {validadePs ? formatDateBR(validadePs) : '—'}
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex items-center justify-end gap-1">
                                  <button type="button" onClick={() => openEdit(g)} className={`${acoesIconBtnClass} hover:text-orange-600`} title="Editar"><IconPencil /></button>
                                  <button type="button" onClick={() => setDeleteGuiaTarget(g)} className={`${acoesIconBtnClass} hover:text-red-600`} title="Excluir"><IconTrash /></button>
                                </div>
                              </td>
                            </tr>
                          );
                        }
                        const a = row.data;
                        return (
                          <tr key={`agencia-${a.id}`} className={`${['bg-white', 'bg-gray-50', 'bg-orange-50'][idx % 3]} border-b border-gray-100 hover:opacity-90 transition-opacity`}>
                            <td className="py-3 px-4"><span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">Agência</span></td>
                            <td className="py-3 px-2 align-middle">
                              <ParceiroAvatarCell src="" initials={a.razaoSocial} />
                            </td>
                            <td className="py-3 px-4">{a.razaoSocial}</td>
                            <td className="py-3 px-3 text-gray-400">—</td>
                            <td className="py-3 px-4">{(a.email ?? '').trim() || '—'}</td>
                            <td className="py-3 px-3 align-middle">
                              <TelefoneCellWhatsapp value={(a.telefone ?? '').trim() || '—'} />
                            </td>
                            <td className="py-3 px-4 text-gray-500">—</td>
                            <td className="py-3 px-4 text-gray-500">—</td>
                            <td className="py-3 px-4">
                              {a.status === 'pendente' ? (
                                <div className="flex flex-col gap-2 items-start">
                                  <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">Em análise</span>
                                  <div className="flex flex-wrap gap-2">
                                    <button type="button" onClick={() => handleAprovarRecusarAgencia(a, 'aprovado')} className={btnAprovarClass}>
                                      Aprovar
                                    </button>
                                    <button type="button" onClick={() => handleAprovarRecusarAgencia(a, 'recusado')} className={btnRecusarClass}>
                                      Recusar
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <span
                                  className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                    a.status === 'aprovado' ? 'bg-green-100 text-green-800' : a.status === 'recusado' ? 'bg-red-100 text-red-800' : 'bg-gray-200 text-gray-700'
                                  }`}
                                >
                                  {statusLabel[a.status]}
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-gray-400">—</td>
                            <td className="py-3 px-4 text-gray-400">—</td>
                            <td className="py-3 px-4">
                              <div className="flex items-center justify-end gap-1">
                                <button type="button" onClick={() => openEditAgencia(a)} className={`${acoesIconBtnClass} hover:text-emerald-600`} title="Editar"><IconPencil /></button>
                                <button type="button" onClick={() => setDeleteAgenciaTarget(a)} className={`${acoesIconBtnClass} hover:text-red-600`} title="Excluir"><IconTrash /></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {sortedFornecedores.length === 0 && (
                  <div className="p-8 text-center text-gray-500">Nenhum parceiro encontrado.</div>
                )}
              </div>
            </>
          ) : formType === 'agencia' ? (
            <div className="max-w-2xl space-y-6">
              <section className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Dados da agência</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CNPJ *</label>
                    <input type="text" value={agenciaForm.cnpj} onChange={(e) => setAgenciaForm((f) => ({ ...f, cnpj: e.target.value }))} className="w-full py-2 px-3 rounded-lg border border-gray-300" placeholder="00.000.000/0000-00" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Razão Social *</label>
                    <input type="text" value={agenciaForm.razaoSocial} onChange={(e) => setAgenciaForm((f) => ({ ...f, razaoSocial: e.target.value }))} className="w-full py-2 px-3 rounded-lg border border-gray-300" placeholder="Razão Social" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">E-mail *</label>
                    <input type="email" value={agenciaForm.email} onChange={(e) => setAgenciaForm((f) => ({ ...f, email: e.target.value }))} className="w-full py-2 px-3 rounded-lg border border-gray-300" placeholder="email@agencia.com" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Telefone/WhatsApp</label>
                    <PhoneInputWithDDI
                      value={agenciaForm.telefone}
                      onChange={(full) => setAgenciaForm((f) => ({ ...f, telefone: full }))}
                      placeholder="(11) 99999-9999"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Endereço completo</label>
                    <textarea value={agenciaForm.enderecoCompleto} onChange={(e) => setAgenciaForm((f) => ({ ...f, enderecoCompleto: e.target.value }))} rows={3} className="w-full py-2 px-3 rounded-lg border border-gray-300" placeholder="Rua, número, bairro, CEP, cidade, estado" />
                  </div>
                </div>
              </section>

              <section className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <h2 className="text-lg font-semibold text-gray-800 mb-2">Guias da agência</h2>
                <p className="text-xs text-gray-500 mb-3">Selecione os guias que trabalham com esta agência.</p>
                <div className="space-y-2 max-h-48 overflow-auto border rounded-lg p-3 bg-white">
                  {guias.length === 0 ? (
                    <p className="text-sm text-gray-500">Nenhum guia cadastrado. Cadastre guias em Parceiros.</p>
                  ) : (
                    guias.map((g) => (
                      <label key={g.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input type="checkbox" checked={agenciaForm.guiaIds.includes(g.id)} onChange={() => toggleGuiaAgencia(g.id)} className="rounded border-gray-300 text-emerald-500" />
                        <span className="text-sm">{g.nome} — {g.email}</span>
                      </label>
                    ))
                  )}
                </div>
              </section>
            </div>
          ) : (
            <div className="w-full max-w-none space-y-6">
              <section className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Definição de perfil</h2>
                <div className="flex flex-wrap gap-6">
                  {PERFIS_PAINEL_PARCEIRO.map(({ value: perfil, label }) => (
                    <label key={perfil} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.perfis.includes(perfil)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setForm((f) => ({ ...f, perfis: [...f.perfis, perfil] }));
                          } else {
                            setForm((f) => ({ ...f, perfis: f.perfis.filter((p) => p !== perfil) }));
                          }
                        }}
                        className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                      />
                      <span className="text-sm font-medium text-gray-700">{label}</span>
                    </label>
                  ))}
                </div>
              </section>

              <div className="flex gap-2 mb-6 overflow-x-auto pb-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => setFormTab('pessoais')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${formTab === 'pessoais' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Informações Pessoais
                </button>
                {form.perfis.includes('GUIA') && (
                  <button
                    type="button"
                    onClick={() => setFormTab('guia')}
                    className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${formTab === 'guia' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    GUIA
                  </button>
                )}
                {form.perfis.includes('TERAPEUTA') && (
                  <button
                    type="button"
                    onClick={() => setFormTab('terapeuta')}
                    className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${formTab === 'terapeuta' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    Terapeuta
                  </button>
                )}
                {form.perfis.includes('COMERCIO_RESTAURANTE') && (
                  <button
                    type="button"
                    onClick={() => setFormTab('comercio')}
                    className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${formTab === 'comercio' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    Comércio / Restaurante
                  </button>
                )}
                {(form.perfis.includes('GESTOR_ATRATIVO') || form.perfis.includes('ANFITRIAO_HOSPEDAGEM')) &&
                  !form.perfis.includes('GUIA') && (
                    <button
                      type="button"
                      onClick={() => setFormTab('gestor_hospedagem')}
                      className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${formTab === 'gestor_hospedagem' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                    >
                      Gestor / Hospedagem
                    </button>
                  )}
              </div>

              {formTab === 'pessoais' && (
                <section className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Informações pessoais</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nome completo *</label>
                      <input type="text" value={form.nome} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} className="w-full py-2 px-3 rounded-lg border border-gray-300" placeholder="Nome" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CPF *</label>
                      <input type="text" value={form.cpf} onChange={(e) => setForm((f) => ({ ...f, cpf: e.target.value }))} className="w-full py-2 px-3 rounded-lg border border-gray-300" placeholder="000.000.000-00" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">E-mail *</label>
                      <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="w-full py-2 px-3 rounded-lg border border-gray-300" placeholder="email@exemplo.com" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Telefone/WhatsApp</label>
                      <PhoneInputWithDDI
                        value={form.telefone}
                        onChange={(full) => setForm((f) => ({ ...f, telefone: full }))}
                        placeholder="(21) 99999-9999"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Chave Pix</label>
                      <input type="text" value={form.chavePix} onChange={(e) => setForm((f) => ({ ...f, chavePix: e.target.value }))} className="w-full py-2 px-3 rounded-lg border border-gray-300" placeholder="123456@" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">BASE (cidade) *</label>
                      <select value={form.baseCidadeId} onChange={(e) => setForm((f) => ({ ...f, baseCidadeId: e.target.value }))} className="w-full py-2 px-3 rounded-lg border border-gray-300" required>
                        <option value="">Selecione a cidade</option>
                        {cidades.map((c) => (
                          <option key={c.id} value={c.id}>{c.cidade}{c.estadoProvincia ? `, ${c.estadoProvincia}` : ''}</option>
                        ))}
                      </select>
                      {editingId && (() => {
                        const guiaAtual = getGuiaById(editingId);
                        const pendente = guiaAtual?.baseCidadePendenteAprovacao;
                        const cidadePendente = pendente ? getCidadeById(pendente) : null;
                        if (!pendente || !cidadePendente) return null;
                        return (
                          <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <p className="text-sm font-medium text-amber-800 mb-2">
                              O guia solicitou troca de cidade base para: {cidadePendente.cidade}{cidadePendente.estadoProvincia ? `, ${cidadePendente.estadoProvincia}` : ''}
                            </p>
                            <p className="text-xs text-amber-700 mb-3">
                              O guia fica inativo até sua decisão. Deseja ativar o guia com a nova BASE?
                            </p>
                            <div className="flex gap-2">
                              <button type="button" onClick={() => handleAprovarCidadeBase(editingId)} className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">
                                Aprovar e ativar guia
                              </button>
                              <button type="button" onClick={() => handleRejeitarCidadeBase(editingId)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700">
                                Não ativar (manter inativo)
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Documento de identidade (RG, CNH ou documento com CPF) *</label>
                      <p className="text-xs text-gray-500 mb-2">
                        Obrigatório para todos os parceiros. Qualquer formato (imagem, PDF, Word, etc.) até {MAX_ANEXO_DOCUMENTO_PARCEIRO_MB}MB.
                      </p>
                      <input
                        type="file"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > MAX_ANEXO_DOCUMENTO_PARCEIRO_MB * 1024 * 1024) {
                            alert(`Arquivo até ${MAX_ANEXO_DOCUMENTO_PARCEIRO_MB}MB.`);
                            return;
                          }
                          const reader = new FileReader();
                          reader.onload = () => setForm((f) => ({ ...f, documentoIdentidadeFornecedor: reader.result as string }));
                          reader.readAsDataURL(file);
                          e.target.value = '';
                        }}
                        className="text-sm"
                      />
                      {form.documentoIdentidadeFornecedor ? (
                        <span className="text-green-600 text-sm block mt-2">Documento anexado</span>
                      ) : null}
                    </div>
                  </div>
                  <div className="mt-6 pt-6 border-t border-gray-200">
                      <h3 className="text-base font-semibold text-gray-800 mb-1">Foto 3x4 (perfil)</h3>
                      <p className="text-xs text-gray-500 mb-3">
                        Foto obrigatória no formato retrato 3x4 (documento). PNG ou JPG até {MAX_IMAGE_SIZE_MB}MB.
                      </p>
                      <div className="flex flex-wrap items-end gap-4">
                        <input type="file" accept="image/png,image/jpeg,image/jpg" onChange={(e) => handleImageFile(e, 'imagemPerfil')} className="text-sm" />
                        {form.imagemPerfil && (
                          <div className="flex flex-col gap-1">
                            <span className="text-xs text-gray-500">Pré-visualização (3x4)</span>
                            <img
                              src={form.imagemPerfil}
                              alt="Foto 3x4 de perfil"
                              className="w-[100px] sm:w-[120px] aspect-[3/4] object-cover border border-gray-200 rounded-md shadow-sm"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                </section>
              )}

              {formTab === 'guia' && form.perfis.includes('GUIA') && (
                <div className="space-y-6">
                  <section className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-800 mb-2">Resumo do perfil</h2>
                    <p className="text-xs text-gray-500 mb-3">Descreva brevemente sua experiência e especialidades como guia.</p>
                    <textarea
                      value={form.resumoPerfil}
                      onChange={(e) => setForm((f) => ({ ...f, resumoPerfil: e.target.value }))}
                      rows={4}
                      className="w-full py-2 px-3 rounded-lg border border-gray-300"
                      placeholder="Ex.: Guia credenciado com 5 anos de experiência em trilhas na região..."
                    />
                  </section>
                  <section className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-800 mb-2">Fotos do trabalho (guia)</h2>
                    <p className="text-xs text-gray-500 mb-3">
                      Até {MAX_FOTOS_TRABALHO_FORNECEDOR} imagens da sua atuação como guia (passeios, trilhas, grupos). PNG ou JPG até {MAX_IMAGE_SIZE_MB}MB cada.
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                      {(form.fotosTrabalhoGuia ?? []).map((url, idx) => (
                        <div key={idx} className="relative rounded-lg border border-gray-200 overflow-hidden bg-white aspect-[4/3]">
                          <img src={url} alt={`Trabalho guia ${idx + 1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeWorkPhoto('fotosTrabalhoGuia', idx)}
                            className="absolute top-1 right-1 text-xs bg-red-600 text-white px-2 py-0.5 rounded shadow"
                          >
                            Remover
                          </button>
                        </div>
                      ))}
                    </div>
                    {(form.fotosTrabalhoGuia ?? []).length < MAX_FOTOS_TRABALHO_FORNECEDOR && (
                      <input type="file" accept="image/png,image/jpeg,image/jpg" onChange={(e) => handleWorkPhotoFile(e, 'fotosTrabalhoGuia')} className="text-sm" />
                    )}
                  </section>
                  <section className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-800 mb-2">Associação do guia</h2>
                    <p className="text-xs text-gray-500 mb-3">
                      Não é obrigatório ter associação. Se escolher associação é necessário anexar documento de identificação. Para registar uma{' '}
                      <strong>nova</strong> associação, use a página{' '}
                      <Link to="/associacao/nova" className="text-orange-600 font-medium hover:underline">
                        Associação
                      </Link>{' '}
                      no menu e depois escolha-a aqui na lista.
                    </p>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Associação (opcional)</label>
                        <select
                          value={form.agenciaId}
                          onChange={(e) => setForm((f) => ({ ...f, agenciaId: e.target.value }))}
                          className="w-full py-2 px-3 rounded-lg border border-gray-300"
                        >
                          <option value="">Sem associação</option>
                          {associacoes.length > 0 && (
                            <optgroup label="Associações">
                              {associacoes.map((a) => (
                                <option key={a.id} value={a.id}>
                                  {a.razaoSocial}
                                  {a.cnpj ? ` - ${a.cnpj}` : ''}
                                </option>
                              ))}
                            </optgroup>
                          )}
                          {agencias.length > 0 && (
                            <optgroup label="Agências">
                              {agencias.map((a) => (
                                <option key={a.id} value={a.id}>
                                  {a.razaoSocial}
                                  {a.cnpj ? ` - ${a.cnpj}` : ''}
                                </option>
                              ))}
                            </optgroup>
                          )}
                        </select>
                      </div>
                      {form.agenciaId && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Documento de identificação *</label>
                          <p className="text-xs text-gray-500 mb-1">Qualquer formato até {MAX_ANEXO_DOCUMENTO_PARCEIRO_MB}MB.</p>
                          <input
                            type="file"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              if (file.size > MAX_ANEXO_DOCUMENTO_PARCEIRO_MB * 1024 * 1024) {
                                alert(`Arquivo até ${MAX_ANEXO_DOCUMENTO_PARCEIRO_MB}MB.`);
                                e.target.value = '';
                                return;
                              }
                              const reader = new FileReader();
                              reader.onload = () => setForm((f) => ({ ...f, documentoIdentificacao: reader.result as string }));
                              reader.readAsDataURL(file);
                              e.target.value = '';
                            }}
                            className="text-sm"
                          />
                          {form.documentoIdentificacao && <span className="text-green-600 text-sm ml-2">Arquivo anexado</span>}
                        </div>
                      )}
                    </div>
                  </section>
                  {renderSecaoDocumentosCertificados()}
                </div>
              )}

              {formTab === 'terapeuta' && form.perfis.includes('TERAPEUTA') && (
                <div className="space-y-6">
                  <section className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-800 mb-2">Resumo do perfil</h2>
                    <p className="text-xs text-gray-500 mb-3">Descreva brevemente sua experiência e especialidades como terapeuta.</p>
                    <textarea
                      value={form.resumoPerfil}
                      onChange={(e) => setForm((f) => ({ ...f, resumoPerfil: e.target.value }))}
                      rows={4}
                      className="w-full py-2 px-3 rounded-lg border border-gray-300"
                      placeholder="Ex.: Terapeuta holístico com formação em massoterapia e reiki..."
                    />
                  </section>
                  <section className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-800 mb-2">Fotos do trabalho (terapeuta)</h2>
                    <p className="text-xs text-gray-500 mb-3">
                      Até {MAX_FOTOS_TRABALHO_FORNECEDOR} imagens da sua prática como terapeuta (ambiente, atendimentos). PNG ou JPG até {MAX_IMAGE_SIZE_MB}MB cada.
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                      {(form.fotosTrabalhoTerapeuta ?? []).map((url, idx) => (
                        <div key={idx} className="relative rounded-lg border border-gray-200 overflow-hidden bg-white aspect-[4/3]">
                          <img src={url} alt={`Trabalho terapeuta ${idx + 1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeWorkPhoto('fotosTrabalhoTerapeuta', idx)}
                            className="absolute top-1 right-1 text-xs bg-red-600 text-white px-2 py-0.5 rounded shadow"
                          >
                            Remover
                          </button>
                        </div>
                      ))}
                    </div>
                    {(form.fotosTrabalhoTerapeuta ?? []).length < MAX_FOTOS_TRABALHO_FORNECEDOR && (
                      <input type="file" accept="image/png,image/jpeg,image/jpg" onChange={(e) => handleWorkPhotoFile(e, 'fotosTrabalhoTerapeuta')} className="text-sm" />
                    )}
                  </section>
                  {renderSecaoDocumentosCertificados()}
                </div>
              )}

              {formTab === 'comercio' && form.perfis.includes('COMERCIO_RESTAURANTE') && (
                <section className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-800 mb-2">Associação ao estabelecimento</h2>
                  <p className="text-xs text-gray-500 mb-3">Comércio / Restaurante precisa se associar a algum estabelecimento cadastrado.</p>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estabelecimento *</label>
                    <select
                      value={form.estabelecimentoId}
                      onChange={(e) => setForm((f) => ({ ...f, estabelecimentoId: e.target.value }))}
                      className="w-full py-2 px-3 rounded-lg border border-gray-300"
                    >
                      <option value="">Selecione o estabelecimento</option>
                      {estabelecimentos.map((e) => (
                        <option key={e.id} value={e.id}>{e.nome}</option>
                      ))}
                    </select>
                    {estabelecimentos.length === 0 && (
                      <p className="text-sm text-amber-600 mt-2">Nenhum estabelecimento cadastrado. Cadastre em Estabelecimentos.</p>
                    )}
                  </div>
                </section>
              )}

              {formTab === 'gestor_hospedagem' &&
                (form.perfis.includes('GESTOR_ATRATIVO') || form.perfis.includes('ANFITRIAO_HOSPEDAGEM')) &&
                !form.perfis.includes('GUIA') && (
                  <div className="space-y-6">
                    <section className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                      <h2 className="text-lg font-semibold text-gray-800 mb-2">Resumo do perfil</h2>
                      <p className="text-xs text-gray-500 mb-3">
                        Gestor de Atrativo e Anfitrião de Hospedagem: descreva sua atuação (alinhado ao formulário do app).
                      </p>
                      <textarea
                        value={form.resumoPerfil}
                        onChange={(e) => setForm((f) => ({ ...f, resumoPerfil: e.target.value }))}
                        rows={4}
                        className="w-full py-2 px-3 rounded-lg border border-gray-300"
                        placeholder="Ex.: Gestão do mirante local / recepção de hóspedes em pousada..."
                      />
                    </section>
                    <section className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                      <h2 className="text-lg font-semibold text-gray-800 mb-2">Fotos do trabalho</h2>
                      <p className="text-xs text-gray-500 mb-3">
                        Até {MAX_FOTOS_TRABALHO_FORNECEDOR} imagens. PNG ou JPG até {MAX_IMAGE_SIZE_MB}MB cada.
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                        {(form.fotosTrabalhoGuia ?? []).map((url, idx) => (
                          <div key={idx} className="relative rounded-lg border border-gray-200 overflow-hidden bg-white aspect-[4/3]">
                            <img src={url} alt={`Trabalho ${idx + 1}`} className="w-full h-full object-cover" />
                            <button
                              type="button"
                              onClick={() => removeWorkPhoto('fotosTrabalhoGuia', idx)}
                              className="absolute top-1 right-1 text-xs bg-red-600 text-white px-2 py-0.5 rounded shadow"
                            >
                              Remover
                            </button>
                          </div>
                        ))}
                      </div>
                      {(form.fotosTrabalhoGuia ?? []).length < MAX_FOTOS_TRABALHO_FORNECEDOR && (
                        <input type="file" accept="image/png,image/jpeg,image/jpg" onChange={(e) => handleWorkPhotoFile(e, 'fotosTrabalhoGuia')} className="text-sm" />
                      )}
                    </section>
                  </div>
                )}
            </div>
          )}
        </div>
      </div>
      {recusarModal && (
        <div className="fixed inset-0 bg-black/50 z-[300] flex items-center justify-center p-4" onClick={() => setRecusarModal(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Reprovar cadastro</h3>
            <p className="text-sm text-gray-600 mb-3">
              Deseja reprovar o cadastro de <strong>{recusarModal.sol.nome}</strong>? Uma mensagem será enviada ao e-mail informado.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Mensagem (opcional)</label>
              <textarea
                value={recusarModal.mensagem}
                onChange={(e) => setRecusarModal((m) => m ? { ...m, mensagem: e.target.value } : null)}
                rows={3}
                className="w-full py-2 px-3 rounded-lg border border-gray-300"
                placeholder="Ex.: Documentação incompleta. Por favor, entre em contato para mais informações."
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button type="button" onClick={() => setRecusarModal(null)} className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors">Cancelar</button>
              <button type="button" onClick={() => handleRecusarSolicitacao(recusarModal.sol, recusarModal.mensagem.trim() || undefined)} className="px-4 py-2.5 rounded-xl font-medium text-white bg-red-600 hover:bg-red-700 transition-colors">Reprovar</button>
            </div>
          </div>
        </div>
      )}
      {recusarParceiroModal && (
        <div className="fixed inset-0 bg-black/50 z-[300] flex items-center justify-center p-4" onClick={() => setRecusarParceiroModal(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Reprovar cadastro</h3>
            <p className="text-sm text-gray-600 mb-3">
              Deseja reprovar o cadastro de <strong>{recusarParceiroModal.nome}</strong>? Uma mensagem será enviada ao e-mail informado.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Mensagem (opcional)</label>
              <textarea
                value={recusarParceiroModal.mensagem}
                onChange={(e) => setRecusarParceiroModal((m) => m ? { ...m, mensagem: e.target.value } : null)}
                rows={3}
                className="w-full py-2 px-3 rounded-lg border border-gray-300"
                placeholder="Ex.: Documentação incompleta. Por favor, entre em contato para mais informações."
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button type="button" onClick={() => setRecusarParceiroModal(null)} className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors">Cancelar</button>
              <button type="button" onClick={handleConfirmarRecusaParceiro} className="px-4 py-2.5 rounded-xl font-medium text-white bg-red-600 hover:bg-red-700 transition-colors">Reprovar</button>
            </div>
          </div>
        </div>
      )}
      {deleteGuiaTarget && (
        <div className="fixed inset-0 bg-black/50 z-[300] flex items-center justify-center p-4" onClick={() => setDeleteGuiaTarget(null)}>
          <div
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex gap-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-7.938 4h15.876c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L2.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Excluir guia</h3>
                <p className="text-gray-600">
                  Tem certeza que deseja excluir <strong>{deleteGuiaTarget.nome}</strong>? Esta ação remove o guia da listagem.
                </p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setDeleteGuiaTarget(null)}
                className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteGuia}
                className="px-4 py-2.5 rounded-xl font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
              >
                Excluir guia
              </button>
            </div>
          </div>
        </div>
      )}
      {deleteAgenciaTarget && (
        <div className="fixed inset-0 bg-black/50 z-[300] flex items-center justify-center p-4" onClick={() => setDeleteAgenciaTarget(null)}>
          <div
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex gap-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-7.938 4h15.876c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L2.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Excluir agência</h3>
                <p className="text-gray-600">
                  Tem certeza que deseja excluir <strong>{deleteAgenciaTarget.razaoSocial}</strong>? Os guias vinculados deixam de estar associados a esta agência.
                </p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setDeleteAgenciaTarget(null)}
                className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteAgencia}
                className="px-4 py-2.5 rounded-xl font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
              >
                Excluir agência
              </button>
            </div>
          </div>
        </div>
      )}
      {inativarGuiaModal && (
        <div
          className="fixed inset-0 bg-black/50 z-[300] flex items-center justify-center p-4"
          onClick={() => setInativarGuiaModal(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Inativar parceiro</h3>
            <p className="text-sm text-gray-600 mb-4">
              A mensagem abaixo será exibida ao guia <strong>{inativarGuiaModal.nome}</strong> (
              {inativarGuiaModal.email}). Você pode personalizar o texto.
            </p>
            <label htmlFor="inativar-motivo" className="block text-sm font-medium text-gray-700 mb-1">
              Motivo da inativação
            </label>
            <textarea
              id="inativar-motivo"
              value={inativarMotivoDraft}
              onChange={(e) => setInativarMotivoDraft(e.target.value)}
              rows={4}
              className="w-full py-2 px-3 rounded-lg border border-gray-300 text-sm"
              placeholder={FORNECEDOR_MSG_CADASTRO_INATIVO_PADRAO}
            />
            <div className="flex gap-3 justify-end mt-4">
              <button
                type="button"
                onClick={() => setInativarGuiaModal(null)}
                className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  const motivo = inativarMotivoDraft.trim() || FORNECEDOR_MSG_CADASTRO_INATIVO_PADRAO;
                  handleSetAtivoInativo(inativarGuiaModal.id, 'inativo', motivo);
                  setInativarGuiaModal(null);
                }}
                className="px-4 py-2.5 rounded-xl font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
              >
                Confirmar inativação
              </button>
            </div>
          </div>
        </div>
      )}
      {statusDropdown &&
        createPortal(
          <div
            id="guia-status-dropdown-portal"
            role="listbox"
            className="fixed z-50 w-36 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
            style={{ top: statusDropdown.top, left: statusDropdown.left }}
          >
            <button
              type="button"
              role="option"
              onClick={() => handleSetAtivoInativo(statusDropdown.guiaId, 'ativo')}
              className="w-full text-left px-3 py-2 text-xs hover:bg-emerald-50 text-emerald-700"
            >
              Ativo
            </button>
            <button
              type="button"
              role="option"
              onClick={() => {
                const g = getGuiaById(statusDropdown.guiaId);
                if (g) {
                  setInativarMotivoDraft(g.motivoInatividade?.trim() || FORNECEDOR_MSG_CADASTRO_INATIVO_PADRAO);
                  setInativarGuiaModal(g);
                  setStatusDropdown(null);
                }
              }}
              className="w-full text-left px-3 py-2 text-xs hover:bg-red-50 text-red-700"
            >
              Inativo
            </button>
          </div>,
          document.body
        )}
    </AdminLayout>
  );
}
